<template>
  <div>
    <div class="extended-filter-block">
      <div class="back-button-wrapper" @click="goBack('/analytics/reports')">
        <LeftOutlined class="back-button" />
      </div>
      <h2>Эталонный профиль</h2>
    </div>

    <div class="card">
      <div class="reference-profile-page" id="print_element">
        <div v-if="isPreloader" class="reference-profile-page__preloader">
          <preloader />
        </div>

        <template v-else-if="referenceReport">
          <div class="reference-profile-page__header">
            <h2 class="reference-profile-page__title">
              Эталонный профиль: {{ profileTitle }}
            </h2>

            <a-button
                data-html2canvas-ignore
                class="primary-button reference-profile-page__print"
                @click="print"
                :loading="isPrinting"
            >
              <template #icon>
                <DownloadOutlined />
              </template>

              Печать
            </a-button>
          </div>

          <ReferenceSection
              :title="`Выборка — ${sampleTotal} ${getWordFormBasedOnTheCount(sampleTotal, 'человек', 'человека', 'человек')}`"
              :opened="openedSections.sample"
              @toggle="toggleSection('sample')"
          >
            <ReferenceSampleBlock />
          </ReferenceSection>

          <ReferenceSection
              title="Результаты по эталонному ЭИ-профилю"
              :opened="openedSections.parameters"
              @toggle="toggleSection('parameters')"
          >
            <ReferenceParametersBlock
                :cards="parameterCards"
                :show-unimportant="showUnimportantParameters"
                @update:show-unimportant="showUnimportantParameters = $event"
                @show-staffs="goToReferenceStaffs"
            />
          </ReferenceSection>

          <ReferenceSection
              title="Структура эталонного ЭИ-профиля"
              :opened="openedSections.structure"
              @toggle="toggleSection('structure')"
          >
            <ReferenceStructureBlock :cards="structureCards" />
          </ReferenceSection>

          <ReferenceSection
              :title="'Дополнительный анализ'"
              :opened="openedSections.additionalAnalysis"
              @toggle="toggleSection('additionalAnalysis')"
          >
            <ManagementAdditionalAnalysisCards :users="users" />
          </ReferenceSection>

          <ReferenceSection
              title="Выводы"
              :opened="openedSections.conclusions"
              @toggle="toggleSection('conclusions')"
          >
            <ReferenceConclusionBlock
                :cards="structureCards"
                @show-staffs="goToReferenceStaffs"
            />
          </ReferenceSection>
        </template>
      </div>
    </div>

    <ReportNextStepPromotionCard
        v-if="referenceReport"
        headerText="Выбирайте лучших, экономьте время и ресурсы"
        bodyText="Мы всегда готовы помочь и рассказать больше о продуктах. Свяжитесь с нами, читайте нас, узнавайте больше об эмоциональном интеллекте."
        :consultCommentProduct="'Эталонный профиль'"
    />
  </div>
</template>

<script>
import { computed, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {DownloadOutlined, LeftOutlined} from "@ant-design/icons-vue";

import Preloader from "@/components/Technical/Preloader.vue";
import ReferenceSection from "@/views/Report/NewProfileReport/ReferenceSection.vue";
import ReferenceStructureBlock from "@/views/Report/NewProfileReport/ReferenceStructureBlock.vue";
import ReferenceConclusionBlock from "@/views/Report/NewProfileReport/ReferenceConclusionBlock.vue";
import ReferenceSampleBlock from "@/views/Report/NewProfileReport/ReferenceSampleBlock.vue";
import ReferenceParametersBlock from "@/views/Report/NewProfileReport/ReferenceParametersBlock.vue";

import baseCards from "./../ParametersCards/parametersCardsList";
import { getWordFormBasedOnTheCount } from "@/views/Report/utils";
import {onBeforeMount} from "@vue/runtime-core";
import ManagementAdditionalAnalysisCards from "@/views/Report/NewProfileReport/ManagementAdditionalAnalysisCards.vue";
import ReportNextStepPromotionCard from "@/components/Cards/ReportNextStepPromotionCard.vue";
import html2pdf from "html2pdf.js";

export default {
  name: "ReferenceProfilePage",

  components: {
    LeftOutlined,
    ReportNextStepPromotionCard,
    ManagementAdditionalAnalysisCards,
    DownloadOutlined,
    Preloader,
    ReferenceParametersBlock,
    ReferenceSampleBlock,
    ReferenceConclusionBlock,
    ReferenceStructureBlock,
    ReferenceSection
  },

  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const isPreloader = ref(false);
    const showUnimportantParameters = ref(true);

    const openedSections = reactive({
      parameters: true,
      structure: true,
      conclusions: true,
      sample: true,
      additionalAnalysis: true,
      theory: true,
    });

    const parameterCards = ref(cloneCardsMap(baseCards));

    const referenceReport = computed(() => {
      return store.state.analytics.referenceReport;
    });

    const users = computed(() => store.state.analytics.referenceReport.users);

    const profileTitle = computed(() => {
      return referenceReport.value?.profile_name || "Специалист по работе с клиентами";
    });

    const sexAgeFemale = computed(() => {
      return referenceReport.value?.sex_and_age_segregation?.female || {};
    });

    const sexAgeMale = computed(() => {
      return referenceReport.value?.sex_and_age_segregation?.male || {};
    });

    const sampleTotal = computed(() => {
      return Number(sexAgeFemale.value?.count || 0) + Number(sexAgeMale.value?.count || 0);
    });

    const structureCards = computed(() => {
      return Array.from(parameterCards.value.values())
          .filter((card) => {
            return card.max !== null && card.max !== undefined;
          })
          .sort((cardA, cardB) => {
            return Number(cardA.graphic_order || 0) - Number(cardB.graphic_order || 0);
          });
    });

    const toggleSection = (key) => {
      openedSections[key] = !openedSections[key];
    };

    const setCardsInfo = () => {
      const report = referenceReport.value;

      if (!report?.score) {
        return;
      }

      const nextCards = cloneCardsMap(baseCards);

      Object.keys(report.score).forEach((key) => {
        const score = report.score[key];
        const lowKpiValue = report.low_kpi_group?.[key];

        if (!score || score.score_from === null || score.score_to === null) {
          return;
        }

        const card = nextCards.get(key);

        if (!card) {
          return;
        }

        card.min = score.score_from;
        card.max = score.score_to;
        card.avg_low_kpi = lowKpiValue;
        card.important = Boolean(score.key);
      });

      parameterCards.value = nextCards;
    };

    const fetchReport = () => {
      if (referenceReport.value) {
        setCardsInfo();
        return;
      }

      isPreloader.value = true;

      store.dispatch("analytics/getReferenceReport", route.params.id)
          .then(() => {
            setCardsInfo();
          })
          .finally(() => {
            isPreloader.value = false;
          });
    };

    const isPrinting = ref(false);

    const print = () => {
      isPrinting.value = true;

      html2pdf(document.getElementById("print_element"), {
        autoPaging: 'text',
        margin: [12, 8, 15, 8],
        filename: "profile" + Date.now() + ".pdf",
        html2canvas: {
          dpi: 192,
          scale: 2,
          windowWidth: 1000,
        },
        pagebreak: {
          mode: ['css', 'legacy'],
          before: ['.pdf-page-break'],
          avoid: ['.pdf-avoid-break'],
        },
      }).then(() => {
        isPrinting.value = false;
      });
    };

    const goToReferenceStaffs = () => {
      router.push("/analytics/reference");
    };

    const goBack = (url) => {
      if (window.history.length > 1) {
        router.back();
      } else {
        router.push(url);
      }
    };

    onBeforeMount(() => {
      fetchReport();
    })

    return {
      isPreloader,
      referenceReport,
      profileTitle,
      openedSections,
      showUnimportantParameters,
      parameterCards,
      structureCards,
      sampleTotal,
      toggleSection,
      print,
      goToReferenceStaffs,
      getWordFormBasedOnTheCount,
      users,
      isPrinting,
      goBack
    };
  },
};

function cloneCardsMap(cardsMap) {
  return new Map(
      Array.from(cardsMap.entries()).map(([key, value]) => {
        return [
          key,
          {
            ...value,
            key,
            structureClass: Array.isArray(value.structureClass)
                ? [...value.structureClass]
                : value.structureClass,
            detailingClass: Array.isArray(value.detailingClass)
                ? [...value.detailingClass]
                : value.detailingClass,
          },
        ];
      })
  );
}
</script>

<style lang="scss" scoped>
.reference-profile-page {
  width: 100%;
  background: #ffffff;
}

.reference-profile-page__preloader {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reference-profile-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 20px;
  margin-bottom: 32px;
}

.reference-profile-page__title {
  margin: 0;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
}

@media (max-width: $mobile-breakpoint) {
  .reference-profile-page__print {
    width: 100%;
  }

  .reference-profile-page__header {
    align-items: flex-start;
    flex-direction: column;
    row-gap: 16px;
  }
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 0;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
}

.back-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  background-color: #FFFFFF;
  border-radius: 25%;
  cursor: pointer;
}
</style>